/**
 * User-saved export presets. Tier 2 Group F sub-piece #8.
 *
 * Built-in presets (`export-presets.ts`) ship a fixed list of common
 * social-media / device dimensions. This module adds a user-managed
 * layer on top: saved presets persist across reloads, surface in the
 * ExportDialog dropdown below the built-ins, and can be deleted.
 *
 * Storage: per-tab IDB store `user-export-presets` (DB v5, see
 * `session-store.ts`). Lifecycle-independent from sessions — they
 * are user-scoped, not per-tab, so `clearSession` / `clearAllSessions`
 * do NOT touch this store.
 *
 * Future enhancement: per-user backend persistence (sync across
 * devices) — would land alongside color-recents sync per the Tier 3
 * Group B follow-up. The current IDB approach is the lighter v1.
 *
 * @see UserExportPreset
 */

import type { ExportFormat } from '../types';
import { openImageEditorDb, SESSION_STORE_NAMES } from './session-store';

/** Maximum number of user presets a single browser stores. Beyond
 *  this the dropdown becomes unscannable; older presets are pushed
 *  out on save. 32 lands generously above the typical per-user count
 *  (the built-in list ships 8 entries by way of comparison). */
export const USER_EXPORT_PRESETS_MAX = 32;

/** Maximum length of the name field. Surfaces in the dropdown
 *  `<option>` text + the delete-confirmation toast. Keep short
 *  enough that long names don't clip the dropdown chevron on
 *  narrow viewports. */
export const USER_EXPORT_PRESET_NAME_MAX = 60;

export interface UserExportPreset {
  /** Stable UUID minted at save time. Used as the IDB key + the
   *  dropdown `<option>` value (prefixed with `user:` so the dialog
   *  can distinguish user presets from built-ins). */
  id: string;
  /** User-facing label. Trimmed at save time. */
  name: string;
  width: number;
  height: number;
  /** Optional saved format. When present, selecting the preset also
   *  flips the dialog's Format dropdown. Omit to leave the user's
   *  current format selection alone (most users will want this —
   *  presets are about dimensions, not encoding). */
  format?: ExportFormat;
  /** Unix epoch ms — used to sort the dropdown (newest first) and
   *  trim oldest entries when `USER_EXPORT_PRESETS_MAX` is exceeded. */
  createdAt: number;
}

/** True iff the open DB actually contains the presets store. Guards
 *  against the dev-only drift where a client's DB version raced ahead of
 *  the store-creation code, leaving the store missing even though the
 *  version says it should exist — `db.transaction()` throws synchronously
 *  in that case (`NotFoundError`), which would reject inside the Promise
 *  executor and escape the outer try/catch. The v6 bump self-heals this
 *  on next load; this guard makes the in-between read degrade to the
 *  documented empty-result fallback instead of an uncaught rejection. */
function hasPresetsStore(db: IDBDatabase): boolean {
  return db.objectStoreNames.contains(SESSION_STORE_NAMES.userExportPresets);
}

/** List all user presets, newest first. Returns an empty array when
 *  the store is empty / missing / IDB is unavailable (Safari private
 *  mode, storage quota, dev schema drift, etc.). */
export async function listUserPresets(): Promise<UserExportPreset[]> {
  try {
    const db = await openImageEditorDb();
    if (!hasPresetsStore(db)) return [];
    return new Promise((resolve) => {
      const tx = db.transaction(SESSION_STORE_NAMES.userExportPresets, 'readonly');
      const store = tx.objectStore(SESSION_STORE_NAMES.userExportPresets);
      const request = store.getAll();
      request.onsuccess = () => {
        const records = (request.result as UserExportPreset[]) ?? [];
        // Sort newest first so the dropdown lists recent saves at
        // the top of the user-presets section.
        records.sort((a, b) => b.createdAt - a.createdAt);
        resolve(records);
      };
      request.onerror = () => {
        console.warn('[image-editor] listUserPresets failed:', request.error);
        resolve([]);
      };
    });
  } catch (err) {
    console.warn('[image-editor] listUserPresets threw:', err);
    return [];
  }
}

/** Persist a new user preset. The returned preset has its `id` +
 *  `createdAt` populated by this function — callers pass only the
 *  human-supplied fields. Names are trimmed + truncated to
 *  `USER_EXPORT_PRESET_NAME_MAX`; empty names fall back to an
 *  auto-generated `${width}×${height}` label so the dropdown row
 *  is never blank. */
export async function saveUserPreset(
  input: Omit<UserExportPreset, 'id' | 'createdAt'>,
): Promise<UserExportPreset | null> {
  const trimmedName =
    (input.name ?? '').trim().slice(0, USER_EXPORT_PRESET_NAME_MAX) ||
    `${input.width}×${input.height}`;
  if (!Number.isFinite(input.width) || !Number.isFinite(input.height)) {
    console.warn('[image-editor] saveUserPreset rejected non-finite dims');
    return null;
  }
  if (input.width < 1 || input.height < 1) {
    console.warn('[image-editor] saveUserPreset rejected non-positive dims');
    return null;
  }
  const preset: UserExportPreset = {
    id: crypto.randomUUID(),
    name: trimmedName,
    width: Math.round(input.width),
    height: Math.round(input.height),
    format: input.format,
    createdAt: Date.now(),
  };
  try {
    const db = await openImageEditorDb();
    if (!hasPresetsStore(db)) {
      console.warn('[image-editor] saveUserPreset: presets store missing, skipping');
      return null;
    }
    return new Promise((resolve) => {
      const tx = db.transaction(SESSION_STORE_NAMES.userExportPresets, 'readwrite');
      const store = tx.objectStore(SESSION_STORE_NAMES.userExportPresets);
      // Insert first so the new preset is visible in the count below.
      store.put(preset);
      // Cap the store — over-cap, delete oldest entries. Cheap because
      // we already paid the read pass via getAll inside the same tx.
      const getAllReq = store.getAll();
      getAllReq.onsuccess = () => {
        const records = (getAllReq.result as UserExportPreset[]) ?? [];
        if (records.length > USER_EXPORT_PRESETS_MAX) {
          records
            .sort((a, b) => a.createdAt - b.createdAt)
            .slice(0, records.length - USER_EXPORT_PRESETS_MAX)
            .forEach((r) => store.delete(r.id));
        }
      };
      tx.oncomplete = () => resolve(preset);
      tx.onerror = () => {
        console.warn('[image-editor] saveUserPreset failed:', tx.error);
        resolve(null);
      };
    });
  } catch (err) {
    console.warn('[image-editor] saveUserPreset threw:', err);
    return null;
  }
}

/** Delete a user preset by id. Idempotent — deleting a non-existent
 *  id is a no-op. */
export async function deleteUserPreset(id: string): Promise<void> {
  try {
    const db = await openImageEditorDb();
    if (!hasPresetsStore(db)) return;
    return new Promise((resolve) => {
      const tx = db.transaction(SESSION_STORE_NAMES.userExportPresets, 'readwrite');
      tx.objectStore(SESSION_STORE_NAMES.userExportPresets).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[image-editor] deleteUserPreset failed:', tx.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] deleteUserPreset threw:', err);
  }
}

/** Delete every user preset. Not currently surfaced in the UI but
 *  useful for tests + a future "Clear all presets" admin affordance. */
export async function clearAllUserPresets(): Promise<void> {
  try {
    const db = await openImageEditorDb();
    if (!hasPresetsStore(db)) return;
    return new Promise((resolve) => {
      const tx = db.transaction(SESSION_STORE_NAMES.userExportPresets, 'readwrite');
      tx.objectStore(SESSION_STORE_NAMES.userExportPresets).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[image-editor] clearAllUserPresets failed:', tx.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] clearAllUserPresets threw:', err);
  }
}
